import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { LtiClaims } from '../types';
import { getUserRole, hasRole, hasAnyRole, canAccessAdmin, canVerifyOTJ, canManageApprentices, UserRole, getDefaultRoute } from '../utils/roles';

interface AuthContextType {
  user: LtiClaims | null;
  isAuthenticated: boolean;
  userRole: UserRole;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  canAccessAdmin: boolean;
  canVerifyOTJ: boolean;
  canManageApprentices: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<LtiClaims | null>(null);

  useEffect(() => {
    // Check for token in URL (from LTI launch)
    const params = new URLSearchParams(window.location.search);
    const launchToken = params.get('launch_token');

    if (launchToken) {
      login(launchToken);
      // Clean URL and redirect to default route for user role
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Check localStorage
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        try {
          const decoded = jwtDecode<LtiClaims>(storedToken);
          // Check if token is expired
          if (decoded.exp && decoded.exp * 1000 > Date.now()) {
            setUser(decoded);
          } else {
            logout();
          }
        } catch (error) {
          logout();
        }
      }
    }
  }, []);

  const login = (token: string) => {
    try {
      const decoded = jwtDecode<LtiClaims>(token);
      setUser(decoded);
      localStorage.setItem('auth_token', token);
      
      // Redirect to default route for user's role
      const defaultRoute = getDefaultRoute(decoded);
      if (window.location.pathname === '/') {
        window.location.href = defaultRoute;
      }
    } catch (error) {
      console.error('Failed to decode token:', error);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth_token');
  };

  const userRole = getUserRole(user);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        userRole,
        hasRole: (role: UserRole) => hasRole(user, role),
        hasAnyRole: (roles: UserRole[]) => hasAnyRole(user, roles),
        canAccessAdmin: canAccessAdmin(user),
        canVerifyOTJ: canVerifyOTJ(user),
        canManageApprentices: canManageApprentices(user),
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
